import React, { useState } from "react";
import { UserPlus, Sparkles } from "lucide-react";
import { JobSearchResultCandidate } from "../../jobSearch/data/api/JobSearchClient";
import { CandidateDetailModal } from "../../jobSearch/pages/JobSearchResultPage";
import "../JobApplications.css";

const prospectiveCardStyle: React.CSSProperties = {
  background: "#ffffff",
  border: "none",
  borderRadius: "12px",
  padding: "16px",
  marginBottom: "12px",
  boxShadow: "0 1px 6px rgba(99,102,241,0.08)",
};

interface ProspectiveCandidatesColumnProps {
  opportunityId: string;
  candidates: JobSearchResultCandidate[];
  addingId: string | null;
  onAdd: (candidate: JobSearchResultCandidate) => void;
  collapsed: boolean;
}

const SKILL_MATCH_CONFIG: Record<string, { label: string; bg: string; color: string; border: string }> = {
  perfect:   { label: "Perfect Match",  bg: "rgba(16,185,129,0.10)",  color: "#059669", border: "rgba(16,185,129,0.25)"  },
  partial:   { label: "Partial Match",  bg: "rgba(245,158,11,0.10)",  color: "#d97706", border: "rgba(245,158,11,0.25)"  },
  mentioned: { label: "Mentioned",      bg: "rgba(99,102,241,0.10)",  color: "#4f46e5", border: "rgba(99,102,241,0.25)"  },
  no_match:  { label: "Low Match",      bg: "rgba(148,163,184,0.12)", color: "#64748b", border: "rgba(148,163,184,0.30)" },
};

const SCORE_COLOR = (score: number) => {
  if (score >= 80) return { fg: "#059669", bg: "rgba(16,185,129,0.12)", ring: "#10b981" };
  if (score >= 60) return { fg: "#d97706", bg: "rgba(245,158,11,0.12)",  ring: "#f59e0b" };
  return             { fg: "#64748b", bg: "rgba(148,163,184,0.12)",       ring: "#94a3b8" };
};

const AVATAR_COLORS = [
  { bg: "rgba(99,102,241,0.15)",  fg: "#4f46e5" },
  { bg: "rgba(16,185,129,0.15)",  fg: "#059669" },
  { bg: "rgba(245,158,11,0.15)",  fg: "#d97706" },
  { bg: "rgba(139,92,246,0.15)",  fg: "#7c3aed" },
];

const MatchedSkillChip: React.FC<{ skill: string }> = ({ skill }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: "3px",
    padding: "3px 8px", borderRadius: "20px",
    fontSize: "0.67rem", fontWeight: 600,
    background: "rgba(99,102,241,0.08)",
    color: "#4f46e5",
    border: "1px solid rgba(99,102,241,0.18)",
    whiteSpace: "nowrap" as const,
  }}>
    {skill}
  </span>
);

const ProspectiveCandidateCard: React.FC<{
  candidate: JobSearchResultCandidate;
  onAdd: (c: JobSearchResultCandidate) => void;
  adding: boolean;
}> = ({ candidate, onAdd, adding }) => {
  const [modalOpen, setModalOpen] = useState(false);

  const name = `${candidate.first_name || ""} ${candidate.last_name || ""}`.trim() || candidate.email_address;
  const initials = name.split(" ").map(p => p[0]?.toUpperCase() || "").join("").slice(0, 2) || "?";
  const score = candidate.match_score ?? 0;
  const scoreStyle = SCORE_COLOR(score);
  const avatarStyle = AVATAR_COLORS[(name.charCodeAt(0) || 0) % AVATAR_COLORS.length];

  const currentRole = candidate.experience_timeline?.[0]?.role || "";
  const currentOrg  = candidate.experience_timeline?.[0]?.organization || "";

  const skillMatchKey = (candidate.skill_match as string) || "no_match";
  const skillMatchCfg = SKILL_MATCH_CONFIG[skillMatchKey] ?? SKILL_MATCH_CONFIG.no_match;

  // Use matched_skills (meaningful) instead of raw skills
  const displaySkills = (candidate.matched_skills || candidate.skills || []).slice(0, 3);

  return (
    <>
      <div style={{
        background: "#ffffff",
        borderRadius: "12px",
        marginBottom: "12px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06), 0 2px 12px rgba(99,102,241,0.06)",
        overflow: "hidden",
      }}>

        {/* ── Top color accent strip ── */}
        <div style={{
          height: "3px",
          background: `linear-gradient(90deg, ${scoreStyle.ring} 0%, rgba(139,92,246,0.6) 100%)`,
        }} />

        <div style={{ padding: "14px 14px 0 14px" }}>

          {/* ── Header: avatar + name + score ── */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>

            {/* Avatar */}
            <div style={{
              flexShrink: 0,
              width: 40, height: 40,
              borderRadius: "50%",
              background: avatarStyle.bg,
              color: avatarStyle.fg,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "0.85rem", fontWeight: 800,
            }}>
              {initials}
            </div>

            {/* Name + email */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontWeight: 700, fontSize: "0.88rem", color: "#1e293b",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                {name}
              </div>
              {candidate.email_address && (
                <div style={{
                  fontSize: "0.68rem", color: "#94a3b8", marginTop: "2px",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {candidate.email_address}
                </div>
              )}
            </div>

            {/* Match score badge */}
            <div style={{
              flexShrink: 0,
              display: "flex", flexDirection: "column", alignItems: "center",
              padding: "6px 10px", borderRadius: "10px",
              background: scoreStyle.bg,
              border: `1.5px solid ${scoreStyle.ring}33`,
            }}>
              <span style={{ fontSize: "1rem", fontWeight: 800, color: scoreStyle.fg, lineHeight: 1 }}>
                {score}<span style={{ fontSize: "0.6rem" }}>%</span>
              </span>
              <span style={{ fontSize: "0.58rem", fontWeight: 700, color: scoreStyle.fg, opacity: 0.8, marginTop: "1px", letterSpacing: "0.3px" }}>
                MATCH
              </span>
            </div>
          </div>

          {/* ── Skill match quality badge ── */}
          <div style={{ marginTop: "12px" }}>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: "5px",
              padding: "4px 10px", borderRadius: "20px",
              background: skillMatchCfg.bg,
              border: `1px solid ${skillMatchCfg.border}`,
              color: skillMatchCfg.color,
              fontSize: "0.7rem", fontWeight: 700,
            }}>
              <span style={{
                width: 6, height: 6, borderRadius: "50%",
                background: skillMatchCfg.color, display: "inline-block",
              }} />
              {skillMatchCfg.label}
            </span>
          </div>


        </div>

        {/* ── Footer actions ── */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "10px 14px 12px",
          marginTop: "12px",
          borderTop: "1px solid rgba(226,232,240,0.7)",
        }}>
          <button
            onClick={() => setModalOpen(true)}
            style={{
              fontSize: "0.75rem", color: "#6366f1", fontWeight: 600,
              background: "none", border: "1px solid rgba(99,102,241,0.25)",
              borderRadius: "7px", cursor: "pointer",
              padding: "5px 12px",
              transition: "background 0.15s",
            }}
          >
            Quick View
          </button>
          <button
            onClick={() => onAdd(candidate)}
            disabled={adding}
            style={{
              background: adding ? "#e2e8f0" : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              border: "none", borderRadius: "7px", padding: "5px 12px",
              cursor: adding ? "not-allowed" : "pointer",
              color: adding ? "#94a3b8" : "#fff",
              fontSize: "0.75rem", fontWeight: 700,
              display: "flex", alignItems: "center", gap: "4px",
              transition: "opacity 0.2s",
            }}
          >
            <UserPlus size={12} />
            {adding ? "Adding…" : "Add to Job"}
          </button>
        </div>
      </div>

      {modalOpen && (
        <CandidateDetailModal
          candidate={candidate}
          index={0}
          total={1}
          onNavigate={() => {}}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
};


export const ProspectiveCandidatesColumn: React.FC<ProspectiveCandidatesColumnProps> = ({
  candidates,
  addingId,
  onAdd,
  collapsed
}) => {
  if (collapsed) return null;

  return (
    <div className="prospective-gleam-col">
      <div className="kan-column-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span className="kan-column-title" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span className="kan-stage-dot" style={{ background: "#6366f1" }} />
            PROSPECTIVE
          </span>
          <span className="kan-column-count">{candidates.length}</span>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <span style={{
            background: "rgba(99,102,241,0.10)",
            color: "#4f46e5",
            border: "1px solid rgba(99,102,241,0.25)",
            fontSize: "0.65rem", fontWeight: 700, padding: "3px 8px", borderRadius: "6px",
            display: "flex", alignItems: "center", gap: "4px",
          }}>
            <Sparkles size={10} />
            AI Pool
          </span>
        </div>
      </div>

      <div className="kan-column-body">
        {candidates.map((c) => (
          <ProspectiveCandidateCard
            key={c.candidate_id}
            candidate={c}
            onAdd={onAdd}
            adding={addingId === c.candidate_id}
          />
        ))}
      </div>
    </div>
  );
};
